/**
 * Compresses an image file (JPEG, PNG, WEBP, HEIC camera captures) before uploading.
 * Downscales images larger than 2048px and compresses to quality 0.85 JPEG.
 * Returns the original file untouched if it is not an image or if compression fails.
 */
export async function compressImageIfNeeded(
  file: File,
  maxDimension = 2048,
  quality = 0.85,
): Promise<File> {
  // If not an image, return original file
  if (
    !file.type ||
    (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name))
  ) {
    return file;
  }

  // If already a small JPEG (< 600 KB), no need to compress
  if (file.size <= 600 * 1024 && (file.type === 'image/jpeg' || file.type === 'image/jpg')) {
    return file;
  }

  return new Promise<File>((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            resolve(file);
          } else {
            const fileName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
            const compressedFile = new File([blob], fileName, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
}
